import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function PaymentLinksFeature() {
  useSEO({
    title: "Payment Links",
    description: "Compose payable moments in seconds. Branded payment links, Stripe-secure checkout, instant settlement to your y ℶα wallet.",
    path: "/atelier/payment-links",
  });
  return (
    <main data-testid="atelier-payment-links" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="atelier-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to Atelier
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 01 / Atelier · Payment Links</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              A link is a<br/><span className="italic gold-text">payable moment.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              Compose, send, and settle in three taps. Each y ℶα payment link is a Stripe-secured checkout — branded with your name, priced in your currency, and credited to your wallet the instant your patron presses pay.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" data-testid="atelier-cta-register" className="btn-gold inline-flex items-center gap-2">Open a merchant account <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">I already have an account</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-2">Sample link</div>
              <div className="font-display text-2xl gold-text break-all">y ℶα/pay/atelier-couture-no-iv</div>
              <hr className="my-6 border-white/10"/>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Amount</span><span className="font-mono">€ 2,400.00</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Currency</span><span>EUR</span></div>
              <div className="flex justify-between text-sm py-2"><span className="text-muted-fg">Status</span><span className="text-gold">● Active</span></div>
              <button disabled className="mt-6 w-full btn-gold opacity-70 cursor-not-allowed">Preview — sign in to create your own</button>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["Branded checkout", "Your title, currency, and tone. Patrons see your brand — not ours."],
            ["Multi-currency", "Price in EUR, USD, GBP, RON and more. We settle to your wallet in real time."],
            ["Stripe-secure", "PCI-DSS Level 1, 3D Secure, Apple Pay, Google Pay — all handled by Stripe."],
            ["Reusable or one-shot", "Toggle a link active/inactive. Cap usage. Mint a fresh URL whenever you wish."],
            ["Instant ledger", "Every successful payment appears on your transactions feed in seconds."],
            ["Zero hidden fees", "You see exactly what Stripe charges. We add nothing on top."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24">
          <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— How it moves</div>
          <ol className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5 border hairline-strong">
            {[
              ["01", "Compose", "Title, amount, currency. That's it."],
              ["02", "Share", "Paste the URL into email, DM, invoice, QR code."],
              ["03", "Patron pays", "Stripe Checkout opens. Card, Apple Pay, Google Pay."],
              ["04", "Settle", "Funds arrive in your y ℶα wallet. Withdraw anytime."],
            ].map(([n, t, b]) => (
              <li key={n} className="bg-bg p-8">
                <div className="font-display text-3xl gold-text">{n}</div>
                <div className="mt-3 font-display text-xl">{t}</div>
                <p className="mt-2 text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            One link. <span className="italic gold-text">Every rail.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Create your first link <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
